import React from "react";
import { Article, ArticlePage, ArticleParameters, ArticleSort, ArticleSorts } from "../../../../amorg-common";
import { articlesClientApi } from "../../../../api/articlesClientApi";
import { OnOff } from "../../../../designSystem/inputs/OnOff";
import { LoadingPageOverlay } from "../../../../designSystem/LoadingPageOverlay";
import { Table, TableHeaderWithSort, TableRowWithOnClick } from "../../../../designSystem/Table";
import { Button } from "../../../../designSystem/button/Button";
import { Modal } from "../../../../designSystem/Modal";
import clone from "clone";
import { Flex, FlexItem } from "../../../../designSystem/compositeComponents/FlexHelpers";
import { toDateString, toDateTimeString } from "../../../../utils/date";
import { P } from "../../../../designSystem/typography/Body";
import { Label } from "../../../../designSystem/Label";
import { LinkButton } from "../../../../designSystem/button/LinkButton";
import { ArticleFilter, ArticleFilterPanel } from "./ArticleFilterPanel";
import { Toggletip } from "../../../../designSystem/Toggletip";

type FilteredArticleTableProps = {
    subscriberId?: string,
    listId?: string,
    buttons?: React.ReactNode[],
    latestNote?: boolean,
    subscriptionCount?: boolean,
    warnings?: boolean,
    preview?: boolean,
    goTo?: boolean,
    onRowClick?: (articleId: string) => void,
};
export function FilteredArticleTable (props: FilteredArticleTableProps): JSX.Element
{
    const [loading, setLoading] = React.useState<boolean>(true);
    const [listing, setListing] = React.useState<ArticlePage>(null);
    const [pageSize, setPageSize] = React.useState(20);

    const [params, setParams] = React.useState<ArticleParameters>({
        subscriberId: props.subscriberId,
        filter: {
            listId: props.listId,
            topicId: undefined,
            typeId: undefined,
            released: undefined
        },
        sortBy: 'title',
        sortOrder: 'asc'
    });

    React.useEffect(() =>
    {
        const getData = async () =>
        {
            setLoading(true);
            const updatedParams = clone(params);
            updatedParams.filter.listId = props.listId;
            const listingResult = await articlesClientApi.getArticleListing(1, pageSize, updatedParams);
            setParams(updatedParams);
            setListing(listingResult);
            setLoading(false);
        };
        getData();
    }, [props.listId]);

    async function handleFilterChange (filter: ArticleFilter)
    {
        setLoading(true);

        const newParams = { ...params, filter };
        setParams(newParams);

        const listingResult = await articlesClientApi.getArticleListing(1, pageSize, newParams);
        setListing(listingResult);

        setLoading(false);
    }

    async function handleClickSubscribe (articleId: string, active: boolean)
    {
        setLoading(true);
        await articlesClientApi.toggleArticleSubscription(props.subscriberId, articleId, active);
        const listingResult = await articlesClientApi.getArticleListing(1, pageSize, params);
        setListing(listingResult);
        setLoading(false);
    }

    async function handlePageSizeChange (pageSize: number)
    {
        setLoading(true);
        setPageSize(pageSize);
        const listingResult = await articlesClientApi.getArticleListing(1, pageSize, params);
        setListing(listingResult);
        setLoading(false);
    }



    async function handleClickSort (sortBy: string)
    {
        if (ArticleSorts.every(s => s !== sortBy))
            return;
        const safeSortBy = sortBy as ArticleSort;


        const isNewSortBy = params.sortBy !== safeSortBy;
        const sortOrder = isNewSortBy
            ? 'asc'
            : params.sortOrder === 'asc'
                ? 'desc'
                : 'asc';

        const newParams = clone(params);
        newParams.sortBy = safeSortBy;
        newParams.sortOrder = sortOrder;

        setLoading(true);
        setParams(newParams);
        const listingResult = await articlesClientApi.getArticleListing(1, pageSize, newParams);
        setListing(listingResult);
        setLoading(false);
    }

    return (
        <div style={{ marginBottom: '32px' }}>
            <LoadingPageOverlay loading={loading} />

            <Flex flexDirection="column" gap={16}>

                <ArticleFilterPanel filter={params.filter} onFilterChange={handleFilterChange} buttons={props.buttons} />


                <Flex flexDirection="column" gap={8}>
                    <Flex flexDirection="row" justifyContent="space-between" alignItems="center" marginTop={20}>
                        <div>
                            {listing?.articles.length > 0 &&
                                <>
                                    {listing.totalItems} lovkrav
                                </>
                            }
                        </div>             
                        <div>
                            {props.buttons}
                        </div>
                    </Flex>

                    <Table>
                        <thead>
                            <tr>
                                {props.warnings === true &&
                                    <>
                                        <TableHeaderWithSort text=""
                                            ascending={params.sortOrder === 'asc'} isActiveSort={params.sortBy === 'missingRequirements'} onClick={() => handleClickSort('missingRequirements')} />
                                    </>
                                }
                                <TableHeaderWithSort text="Liste"
                                    ascending={params.sortOrder === 'asc'} isActiveSort={params.sortBy === 'list'} onClick={() => handleClickSort('list')} />

                                <TableHeaderWithSort text="Emne"
                                    ascending={params.sortOrder === 'asc'} isActiveSort={params.sortBy === 'topic'} onClick={() => handleClickSort('topic')} />

                                <TableHeaderWithSort text="Type"
                                    ascending={params.sortOrder === 'asc'} isActiveSort={params.sortBy === 'type'} onClick={() => handleClickSort('type')} />

                                <TableHeaderWithSort text="Lov udgivet"
                                    ascending={params.sortOrder === 'asc'} isActiveSort={params.sortBy === 'date'} onClick={() => handleClickSort('date')} />

                                <TableHeaderWithSort text="Titel"
                                    ascending={params.sortOrder === 'asc'} isActiveSort={params.sortBy === 'title'} onClick={() => handleClickSort('title')} />

                                <th>Lovnummer</th>

                                <th>Ver.</th>

                                {props.latestNote &&
                                    <TableHeaderWithSort text="Seneste note"
                                        ascending={params.sortOrder === 'asc'} isActiveSort={params.sortBy === 'lastNote'} onClick={() => handleClickSort('lastNote')} />
                                }

                                <TableHeaderWithSort text="Udgivelse"
                                    ascending={params.sortOrder === 'asc'} isActiveSort={params.sortBy === 'releaseDate'} onClick={() => handleClickSort('releaseDate')} />

                                {props.subscriptionCount &&
                                    <th>
                                        Abonnenter
                                    </th>
                                }

                                {props.subscriberId
                                    ? <th>Abonnér</th>
                                    : null
                                }
                                {(props.preview || props.goTo) &&
                                    <th></th>
                                }
                            </tr>
                        </thead>
                        <tbody>
                            {listing?.articles.map(a => 
                            {
                                return (
                                    <TableRowWithOnClick key={a.articleId} onClick={props.onRowClick ? () => props.onRowClick(a.articleId) : undefined}>
                                        {props.warnings &&
                                            <td style={{ verticalAlign: 'middle', width: '24px' }}>
                                                {a.missingRequirements &&
                                                    <Toggletip icon="warning">
                                                        Ingen juridisk forpligtigelse
                                                    </Toggletip>
                                                }
                                            </td>
                                        }
                                        <td style={{ whiteSpace: 'nowrap' }}>{a.list.name}</td>
                                        <td>{a.topic.name}</td>
                                        <td>{a.type.name}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{a.date ? toDateString(a.date) : ''}</td>
                                        <td>{a.title}</td>
                                        <td>{a.reference}</td>
                                        <td>{a.version}</td>
                                        {props.latestNote &&
                                            <td>{a.lastNote
                                                ? <>
                                                    <div>{toDateTimeString(a.lastNote.date)}</div>
                                                    <small>{a.lastNote.user.email}</small>
                                                </>
                                                : null
                                            }
                                            </td>
                                        }
                                        <td style={{textAlign:'center'}}>
                                            {a.release === null
                                                ?   <>-</>
                                                :   toDateString(a.release.releasedDate)
                                        }
                                        </td>
                                        {props.subscriptionCount &&
                                            <td>{a.subscriberCount}</td>
                                        }
                                        {props.subscriberId
                                            ? <td>
                                                <OnOff
                                                    active={a.subscribed}
                                                    onClick={(active) => handleClickSubscribe(a.articleId, active)}
                                                    disabled={a.version === 1 && a.release === null} />
                                            </td>
                                            : null
                                        }
                                        {(props.preview || props.goTo) &&
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <Flex gap={4}>
                                                    {props.preview && (a.release || a.version > 1) &&
                                                        <ArticlePreviewButton articleId={a.articleId} />
                                                    }
                                                    {props.goTo &&
                                                        <LinkButton
                                                            variant={"outlined"}
                                                            icon="interface_actions_externalLink"
                                                            href={`/amorgy/articles/${a.articleId}`} 
                                                        />
                                                    }
                                                </Flex>
                                            </td>                                            
                                        }

                                    </TableRowWithOnClick>
                                );
                            })}
                        </tbody>
                    </Table>
                </Flex>

                {listing?.articles.length === 0 &&
                    <P>Ingen lovkrav fundet</P>
                }

                {listing?.totalItems > pageSize && pageSize === 20 &&

                    <Button variant="outlined" text="Vis flere (100)" onClick={() => handlePageSizeChange(100)} />
                }

                {listing?.totalItems > pageSize && pageSize === 100 &&

                    <Button variant="outlined" text={`Vis alle (${listing.totalItems})`} onClick={() => handlePageSizeChange(9999)} />
                }

                {pageSize > 100 &&

                    <Button variant="outlined" text={`Vis færre (20)`} onClick={() => handlePageSizeChange(20)} />
                }

            </Flex>
        </div>
    );
}


type ArticlePreviewButtonProps = {
    articleId: string,
    buttons?: React.ReactNode[],
};

function ArticlePreviewButton (props: ArticlePreviewButtonProps): JSX.Element
{

    const [show, setShow] = React.useState(false);
    const [articles, setArticles] = React.useState<Article[]>([]);

    async function handleClick (e)
    {
        e.stopPropagation();
        const arts = await articlesClientApi.getArticleVersions(props.articleId);
        setArticles(arts.versions);
        setShow(true);

    }

    const latest = articles.reduce((prev, current) => current.version > prev.version ? current : prev, articles[0]);

    return (
        <>

            <Button variant="outlined" icon="data_files_document" onClick={handleClick} />

            <Modal
                title={"Lovkrav"}
                width="50%"
                showModal={show}
                onCancel={() => setShow(false)}
                content={
                    <Flex flexDirection="column" alignItems='stretch' gap={16}>

                        {latest &&

                            <Flex flexDirection="column" gap={32} key={`${latest.articleVersionId}`}>
                                <Flex flexDirection="column" gap={4}>
                                    <Label>Titel</Label>
                                    <P>{latest.title}</P>
                                </Flex>
                                <Flex flexDirection="row">
                                    <FlexItem width="50%">
                                        <Flex flexDirection="column" gap={4}>
                                            <Label>Lovnummer</Label>
                                            <P>{latest.reference}</P>
                                        </Flex>
                                    </FlexItem>
                                    <FlexItem width="50%">
                                        <Flex flexDirection="column" gap={4}>
                                            <Label>Webadresse</Label>
                                            <Flex flexDirection="row" gap={8}>
                                                {/* <div style={{overflow: "hidden", whiteSpace: "nowrap"}}>
                                            <P>{latest.url}</P>
                                        </div> */}

                                                <LinkButton
                                                    icon="interface_actions_externalLink"
                                                    text={latest.url.slice(0, 42) + '...'}
                                                    href={latest.url}
                                                    variant="filled"
                                                    target="_blank" />
                                            </Flex>
                                        </Flex>
                                    </FlexItem>
                                </Flex>

                                <Flex flexDirection="row">
                                    <FlexItem width="50%">
                                        <Flex flexDirection="column" gap={4}>
                                            <Label>Liste/ emne</Label>
                                            <P>{latest.topic.name}</P>
                                        </Flex>
                                    </FlexItem>
                                    <FlexItem width="50%">
                                        <Flex flexDirection="column" gap={4}>
                                            <Label>Type lovkrav</Label>
                                            <P>{latest.type.name}</P>
                                        </Flex>
                                    </FlexItem>
                                </Flex>

                                <Flex flexDirection="column" gap={4}>
                                    <Label>Dato</Label>
                                    <P>{latest.date ? toDateString(latest.date) : ''}</P>
                                </Flex>

                                <Flex flexDirection="column" gap={4}>
                                    <Label>Beskrivelse</Label>
                                    <P>{latest.description}</P>
                                </Flex>
                            </Flex>
                        }

                    </Flex>
                }
            />
        </>
    );
}

